import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RFQ, RFQStatus, RFQUpdate, RFQDocumentUploadResponse } from '../types';
import { rfqApi } from '../services/api';
import { getStatusLabel, getStatusColor } from '../utils/statusDisplay';
import { getIsAdmin } from '../utils/auth';
import ConveyorDrawingUpload from './ConveyorDrawingUpload';
import ConveyorStationsConfig from './ConveyorStationsConfig';
import ExportDialog from './ExportDialog';
import ProcurementPage from './ProcurementPage';

interface RFQDetailsProps {
    rfqId: string;
    onBack: () => void;
    onUpdated?: () => void;
}

type DetailsView = 'configuration' | 'layout' | 'procurement';

type ParsedDataTab =
    | 'general'
    | 'sequence'
    | 'required'
    | 'free_issued'
    | 'equipment_approved';

const PARSED_TAB_LABELS: Record<ParsedDataTab, string> = {
    general: 'General',
    sequence: 'Sequence of operations',
    required: 'Required equipment',
    free_issued: 'Free-issued equipment',
    equipment_approved: 'Equipment approved for use',
};

const SectionCard: React.FC<{
    eyebrow: string;
    title: string;
    subtitle?: React.ReactNode;
    right?: React.ReactNode;
    children: React.ReactNode;
}> = ({ eyebrow, title, subtitle, right, children }) => (
    <article className="relative bg-white border border-[#141414]/20 shadow-[0_1px_0_0_#141414] mb-6">
        <span className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#141414]" />
        <span className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#141414]" />
        <span className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#141414]" />
        <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#141414]" />

        <div className="px-6 py-4 border-b border-[#141414]/15 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
                <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e]">
                    {eyebrow}
                </div>
                <h2 className="ff-serif text-3xl leading-none mt-1">{title}</h2>
                {subtitle && (
                    <div className="ff-mono text-[11px] text-[#141414]/55 mt-2">
                        {subtitle}
                    </div>
                )}
            </div>
            {right && <div className="shrink-0">{right}</div>}
        </div>

        <div className="px-6 py-5">{children}</div>
    </article>
);

const RFQDetails: React.FC<RFQDetailsProps> = ({ rfqId, onBack, onUpdated }) => {
    const [rfq, setRfq] = useState<RFQ | null>(null);
    const [form, setForm] = useState<RFQUpdate>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isUploadingDocument, setIsUploadingDocument] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [documents, setDocuments] = useState<RFQDocumentUploadResponse[]>([]);
    const [uploadErrorPopup, setUploadErrorPopup] = useState<string | null>(null);
    const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [parsedDataTab, setParsedDataTab] = useState<ParsedDataTab>('general');
    const [detailsView, setDetailsView] = useState<DetailsView>('configuration');
    const [sequenceDraft, setSequenceDraft] = useState<string[] | null>(null);
    const [isSavingSequence, setIsSavingSequence] = useState<boolean>(false);
    const [stationsReloadKey, setStationsReloadKey] = useState<number>(0);
    const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false);

    useEffect(() => {
        const loadDetails = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const [data, existingDocuments] = await Promise.all([
                    rfqApi.get(rfqId),
                    rfqApi.getDocuments(rfqId),
                ]);
                setRfq(data);
                setDocuments(existingDocuments);
                setForm({
                    title: data.title,
                    description: data.description,
                    client_name: data.client_name,
                    part_type: data.part_type,
                    status: data.status,
                });
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load RFQ details');
            } finally {
                setIsLoading(false);
            }
        };

        void loadDetails();
    }, [rfqId]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        if (name === 'status') {
            setForm((prev) => ({ ...prev, status: value as RFQStatus }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            await rfqApi.update(rfqId, form);
            if (onUpdated) {
                onUpdated();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save RFQ');
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenExportDialog = () => {
        setIsExportDialogOpen(true);
    };

    const handleDocumentFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        setSelectedFile(file);
    };

    const handleUploadDocument = async () => {
        if (!selectedFile) {
            setError('Please select a PDF file to upload');
            return;
        }

        setIsUploadingDocument(true);
        setError(null);
        setUploadErrorPopup(null);
        try {
            const response = await rfqApi.uploadDocument(rfqId, selectedFile);
            setDocuments((prev) => [response, ...prev]);
            setSelectedFile(null);
        } catch (err) {
            let details = 'Failed to upload document';
            if (axios.isAxiosError(err)) {
                const responseDetail =
                    typeof err.response?.data?.detail === 'string'
                        ? err.response.data.detail
                        : null;
                details = responseDetail ?? err.message ?? details;
            } else if (err instanceof Error) {
                details = err.message;
            }

            setUploadErrorPopup(details);
            setError(details);
        } finally {
            setIsUploadingDocument(false);
        }
    };

    const handleRemoveDocument = async (documentId: string) => {
        setDeletingDocumentId(documentId);
        setError(null);
        try {
            await rfqApi.deleteDocument(rfqId, documentId);
            setDocuments((prev) => prev.filter((d) => d.id !== documentId));
        } catch (err) {
            let details = 'Failed to remove document';
            if (axios.isAxiosError(err)) {
                const responseDetail =
                    typeof err.response?.data?.detail === 'string'
                        ? err.response.data.detail
                        : null;
                details = responseDetail ?? err.message ?? details;
            } else if (err instanceof Error) {
                details = err.message;
            }
            setError(details);
        } finally {
            setDeletingDocumentId(null);
        }
    };

    const isAdmin = getIsAdmin();
    const latestDocument: RFQDocumentUploadResponse | null =
        documents.length > 0 ? documents[0] : null;
    const latestDocumentId = latestDocument?.id ?? null;

    useEffect(() => {
        if (latestDocument) {
            setSequenceDraft(latestDocument.extracted_data.sequence_of_operations);
        } else {
            setSequenceDraft(null);
        }
    // Re-seed only when the latest document identity changes; in-place edits
    // to the draft must not be overwritten by re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestDocumentId]);

    const isSequenceDirty =
        latestDocument !== null &&
        sequenceDraft !== null &&
        JSON.stringify(sequenceDraft) !==
            JSON.stringify(latestDocument.extracted_data.sequence_of_operations);

    const handleSequenceStepChange = (index: number, value: string) => {
        setSequenceDraft((prev) => {
            if (!prev) return prev;
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    const handleSequenceStepRemove = (index: number) => {
        setSequenceDraft((prev) =>
            prev ? prev.filter((_, i) => i !== index) : prev
        );
    };

    const handleSequenceStepMove = (index: number, direction: 'up' | 'down') => {
        setSequenceDraft((prev) => {
            if (!prev) return prev;
            const target = direction === 'up' ? index - 1 : index + 1;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const handleSequenceStepAdd = () => {
        setSequenceDraft((prev) => (prev ? [...prev, ''] : ['']));
    };

    const handleSaveSequence = async () => {
        if (!latestDocument || sequenceDraft === null) return;
        setIsSavingSequence(true);
        setError(null);
        try {
            const cleaned = sequenceDraft
                .map((step) => step.trim())
                .filter((step) => step.length > 0);
            const response = await rfqApi.updateDocumentSequence(
                rfqId,
                latestDocument.id,
                cleaned
            );
            setDocuments((prev) =>
                prev.map((doc) => (doc.id === response.id ? response : doc))
            );
            setSequenceDraft(response.extracted_data.sequence_of_operations);
            // Backend reset station step assignment; force the stations panel to refetch.
            setStationsReloadKey((n) => n + 1);
        } catch (err) {
            let details = 'Failed to save sequence';
            if (axios.isAxiosError(err)) {
                const responseDetail =
                    typeof err.response?.data?.detail === 'string'
                        ? err.response.data.detail
                        : null;
                details = responseDetail ?? err.message ?? details;
            } else if (err instanceof Error) {
                details = err.message;
            }
            setError(details);
        } finally {
            setIsSavingSequence(false);
        }
    };

    if (isLoading) {
        return (
            <div className="ff-mono text-sm text-[#141414]/55">
                Loading RFQ details…
            </div>
        );
    }

    if (!rfq) {
        return (
            <div>
                <div className="mb-4 border border-[#c2251a] bg-[#c2251a]/10 text-[#c2251a] px-4 py-3 ff-mono text-sm">
                    RFQ not found.
                </div>
                <button
                    type="button"
                    onClick={onBack}
                    className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#141414]/5 transition"
                >
                    ← Back to RFQs
                </button>
            </div>
        );
    }

    const inputClass =
        'w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm';
    const labelClass =
        'block ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 mb-1';

    return (
        <div>
            <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
                <button
                    type="button"
                    onClick={onBack}
                    className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#141414]/5 transition"
                >
                    ← Back to RFQs
                </button>
                <div className="flex items-center gap-2">
                    <span
                        className={`px-2 py-1 inline-flex ff-mono text-[10px] uppercase tracking-[0.15em] ${getStatusColor(
                            rfq.status
                        )}`}
                    >
                        {getStatusLabel(rfq.status)}
                    </span>
                    <button
                        type="button"
                        onClick={handleOpenExportDialog}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] transition"
                    >
                        Export
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-4 border border-[#c2251a] bg-[#c2251a]/10 text-[#c2251a] px-4 py-3 ff-mono text-sm">
                    {error}
                </div>
            )}

            <nav
                className="mb-6 flex border border-[#141414]/25 bg-white/60 w-fit"
                role="tablist"
                aria-label="RFQ sections"
            >
                <DetailsTab
                    active={detailsView === 'configuration'}
                    onClick={() => setDetailsView('configuration')}
                    label="Configuration"
                />
                {isAdmin && (
                    <DetailsTab
                        active={detailsView === 'layout'}
                        onClick={() => setDetailsView('layout')}
                        label="Layout"
                    />
                )}
                <DetailsTab
                    active={detailsView === 'procurement'}
                    onClick={() => setDetailsView('procurement')}
                    label="Procurement"
                />
            </nav>

            {isExportDialogOpen && (
                <ExportDialog
                    rfqId={rfqId}
                    onClose={() => setIsExportDialogOpen(false)}
                />
            )}

            {uploadErrorPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#141414]/50 px-4">
                    <article className="relative bg-white border border-[#141414]/30 shadow-[0_1px_0_0_#141414] w-full max-w-lg">
                        <span className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#141414]" />
                        <span className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#141414]" />
                        <span className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#141414]" />
                        <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#141414]" />
                        <div className="p-5">
                            <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#c2251a]">
                                Error
                            </div>
                            <h3 className="ff-serif text-2xl leading-none mt-1 mb-3">
                                Upload failed
                            </h3>
                            <p className="ff-mono text-xs text-[#141414] whitespace-pre-wrap">
                                {uploadErrorPopup}
                            </p>
                            <div className="mt-4 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setUploadErrorPopup(null)}
                                    className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] transition"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </article>
                </div>
            )}

            {detailsView === 'procurement' && (
                <section className="bg-white border border-[#141414]/20 shadow-[0_1px_0_0_#141414] relative mb-6">
                    <span className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#141414]" />
                    <span className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#141414]" />
                    <span className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#141414]" />
                    <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#141414]" />
                    <div className="px-6 py-4 border-b border-[#141414]/15">
                        <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e]">
                            Procurement
                        </div>
                        <h2 className="ff-serif text-3xl leading-none mt-1">
                            Bill of materials
                        </h2>
                        <p className="ff-mono text-[11px] text-[#141414]/55 mt-2">
                            Aggregated from conveyor stations. Updates automatically as
                            equipment changes. Assign vendors and track ordering progress inline.
                        </p>
                    </div>
                    <div className="px-6 py-5">
                        <ProcurementPage rfqId={rfqId} />
                    </div>
                </section>
            )}

            {detailsView === 'layout' && isAdmin && (
                <SectionCard
                    eyebrow="Layout"
                    title="Conveyor drawing"
                    subtitle="Upload a DXF to populate stations and equipment from the layout."
                >
                    <ConveyorDrawingUpload
                        rfqId={rfqId}
                        onApplied={() => setStationsReloadKey((n) => n + 1)}
                    />
                </SectionCard>
            )}

            {detailsView === 'configuration' && (
            <>
            <SectionCard eyebrow="Request" title={rfq.title}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass} htmlFor="title">
                            Title
                        </label>
                        <input
                            id="title"
                            name="title"
                            value={form.title ?? ''}
                            onChange={handleChange}
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className={labelClass} htmlFor="client_name">
                            Client name
                        </label>
                        <input
                            id="client_name"
                            name="client_name"
                            value={form.client_name ?? ''}
                            onChange={handleChange}
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className={labelClass} htmlFor="part_type">
                            Part type
                        </label>
                        <input
                            id="part_type"
                            name="part_type"
                            value={form.part_type ?? ''}
                            onChange={handleChange}
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className={labelClass} htmlFor="status">
                            Status
                        </label>
                        <select
                            id="status"
                            name="status"
                            value={form.status ?? RFQStatus.WAITING_FOR_DOCUMENTS}
                            onChange={handleChange}
                            className={inputClass}
                        >
                            {Object.values(RFQStatus).map((status) => (
                                <option key={status} value={status}>
                                    {getStatusLabel(status)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="mt-4">
                    <label className={labelClass} htmlFor="description">
                        Description
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        value={form.description ?? ''}
                        onChange={handleChange}
                        rows={4}
                        className={`${inputClass} resize-y`}
                    />
                </div>

                <div className="mt-5 flex justify-end">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] disabled:opacity-50 transition"
                    >
                        {isSaving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </SectionCard>

            <SectionCard
                eyebrow="Source"
                title="Documents"
                subtitle="Upload PDFs to extract program details automatically."
            >
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <input
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={handleDocumentFileChange}
                        className="block w-full ff-mono text-xs text-[#141414] file:mr-4 file:border file:border-[#141414]/25 file:bg-white file:px-3 file:py-2 file:ff-mono file:text-[10px] file:uppercase file:tracking-wider file:text-[#141414] hover:file:bg-[#141414]/5"
                    />
                    <button
                        type="button"
                        onClick={handleUploadDocument}
                        disabled={isUploadingDocument || !selectedFile}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] disabled:opacity-50 transition shrink-0"
                    >
                        {isUploadingDocument ? 'Uploading…' : 'Upload PDF'}
                    </button>
                </div>

                {documents.length === 0 ? (
                    <div className="mt-4 ff-mono text-xs text-[#141414]/55">
                        No documents uploaded yet.
                    </div>
                ) : (
                    <div className="mt-4 space-y-2">
                        {documents.map((document) => (
                            <div
                                key={document.id}
                                className={`relative flex items-center justify-between gap-3 border border-[#141414]/15 bg-[#141414]/[0.02] px-3 py-2 ${isAdmin ? 'pr-12' : ''}`}
                            >
                                <div className="min-w-0">
                                    <span className="ff-mono text-xs text-[#141414] font-medium truncate block">
                                        {document.filename}
                                    </span>
                                    <span className="ff-mono text-[10px] text-[#141414]/55">
                                        {new Date(document.uploaded_at).toLocaleString()}
                                    </span>
                                </div>
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveDocument(document.id)}
                                        disabled={deletingDocumentId === document.id}
                                        aria-label="Remove document"
                                        title="Remove document"
                                        className="absolute top-1/2 right-2 -translate-y-1/2 flex h-7 w-7 shrink-0 items-center justify-center text-[#141414]/55 hover:bg-[#c2251a]/10 hover:text-[#c2251a] disabled:cursor-not-allowed disabled:opacity-40 transition"
                                    >
                                        {deletingDocumentId === document.id ? (
                                            <span className="ff-mono text-xs">…</span>
                                        ) : (
                                            <span className="text-lg leading-none" aria-hidden>
                                                ×
                                            </span>
                                        )}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </SectionCard>

            <SectionCard
                eyebrow="Extracted"
                title="Parsed data"
                subtitle={
                    latestDocument ? (
                        <>
                            Showing data from the latest upload:{' '}
                            <span className="text-[#141414] font-medium">
                                {latestDocument.filename}
                            </span>
                            <span className="text-[#141414]/45">
                                {' '}
                                ({new Date(latestDocument.uploaded_at).toLocaleString()})
                            </span>
                        </>
                    ) : (
                        'Upload a PDF above to extract and display parsed data.'
                    )
                }
            >
                <div
                    className="flex flex-wrap gap-0 border-b border-[#141414]/15 -mx-6 px-6"
                    role="tablist"
                    aria-label="Parsed data categories"
                >
                    {(Object.keys(PARSED_TAB_LABELS) as ParsedDataTab[]).map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            role="tab"
                            aria-selected={parsedDataTab === tab}
                            onClick={() => setParsedDataTab(tab)}
                            className={`ff-mono text-[10px] uppercase tracking-[0.15em] px-3 py-2 -mb-px border-b-2 transition ${
                                parsedDataTab === tab
                                    ? 'border-[#e0461e] text-[#141414]'
                                    : 'border-transparent text-[#141414]/55 hover:text-[#141414]'
                            }`}
                        >
                            {PARSED_TAB_LABELS[tab]}
                        </button>
                    ))}
                </div>

                <div className="pt-5 min-h-[8rem]" role="tabpanel">
                    {!latestDocument && (
                        <p className="ff-mono text-xs text-[#141414]/55">
                            No parsed data yet.
                        </p>
                    )}
                    {latestDocument && parsedDataTab === 'general' && (
                        <div>
                            {(latestDocument.extracted_data.general_requirements ?? [])
                                .length === 0 ? (
                                <p className="ff-mono text-xs text-[#141414]/55">
                                    None extracted.
                                </p>
                            ) : (
                                <ul className="list-disc list-inside space-y-1.5 ff-mono text-xs text-[#141414]">
                                    {latestDocument.extracted_data.general_requirements.map(
                                        (req, index) => (
                                            <li key={`latest-gen-${index}`}>{req}</li>
                                        )
                                    )}
                                </ul>
                            )}
                        </div>
                    )}
                    {latestDocument && parsedDataTab === 'sequence' && (
                        <div>
                            {isAdmin && sequenceDraft !== null ? (
                                <div className="space-y-2">
                                    {sequenceDraft.length === 0 && (
                                        <p className="ff-mono text-xs text-[#141414]/55">
                                            No operations yet. Use “Add step” below to add one.
                                        </p>
                                    )}
                                    {sequenceDraft.map((step, index) => (
                                        <div
                                            key={`seq-edit-${index}`}
                                            className="flex items-center gap-2"
                                        >
                                            <span className="ff-mono text-[10px] text-[#141414]/55 w-6 shrink-0 text-right">
                                                {index + 1}.
                                            </span>
                                            <input
                                                value={step}
                                                onChange={(e) =>
                                                    handleSequenceStepChange(
                                                        index,
                                                        e.target.value
                                                    )
                                                }
                                                className={inputClass}
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSequenceStepMove(index, 'up')
                                                }
                                                disabled={index === 0}
                                                aria-label="Move step up"
                                                title="Move step up"
                                                className="flex h-7 w-7 shrink-0 items-center justify-center text-[#141414]/55 hover:bg-[#141414]/5 hover:text-[#141414] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#141414]/55 transition"
                                            >
                                                <span
                                                    className="text-base leading-none"
                                                    aria-hidden
                                                >
                                                    ↑
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSequenceStepMove(index, 'down')
                                                }
                                                disabled={
                                                    index === sequenceDraft.length - 1
                                                }
                                                aria-label="Move step down"
                                                title="Move step down"
                                                className="flex h-7 w-7 shrink-0 items-center justify-center text-[#141414]/55 hover:bg-[#141414]/5 hover:text-[#141414] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#141414]/55 transition"
                                            >
                                                <span
                                                    className="text-base leading-none"
                                                    aria-hidden
                                                >
                                                    ↓
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSequenceStepRemove(index)
                                                }
                                                aria-label="Remove step"
                                                title="Remove step"
                                                className="flex h-7 w-7 shrink-0 items-center justify-center text-[#141414]/55 hover:bg-[#c2251a]/10 hover:text-[#c2251a] transition"
                                            >
                                                <span
                                                    className="text-lg leading-none"
                                                    aria-hidden
                                                >
                                                    ×
                                                </span>
                                            </button>
                                        </div>
                                    ))}
                                    <div className="flex items-center justify-between gap-2 pt-2">
                                        <button
                                            type="button"
                                            onClick={handleSequenceStepAdd}
                                            className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#141414]/5 transition"
                                        >
                                            + Add step
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSaveSequence}
                                            disabled={
                                                !isSequenceDirty || isSavingSequence
                                            }
                                            className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] disabled:opacity-50 transition"
                                        >
                                            {isSavingSequence ? 'Saving…' : 'Save sequence'}
                                        </button>
                                    </div>
                                </div>
                            ) : latestDocument.extracted_data.sequence_of_operations
                                  .length === 0 ? (
                                <p className="ff-mono text-xs text-[#141414]/55">
                                    No operations extracted.
                                </p>
                            ) : (
                                <ol className="list-decimal list-inside space-y-1.5 ff-mono text-xs text-[#141414]">
                                    {latestDocument.extracted_data.sequence_of_operations.map(
                                        (step, index) => (
                                            <li key={`latest-step-${index}`}>{step}</li>
                                        )
                                    )}
                                </ol>
                            )}
                        </div>
                    )}
                    {latestDocument && parsedDataTab === 'required' && (
                        <div>
                            {(latestDocument.extracted_data.required_equipment ?? [])
                                .length === 0 ? (
                                <p className="ff-mono text-xs text-[#141414]/55">
                                    None listed.
                                </p>
                            ) : (
                                <ul className="list-disc list-inside space-y-1.5 ff-mono text-xs text-[#141414]">
                                    {latestDocument.extracted_data.required_equipment.map(
                                        (line, index) => (
                                            <li key={`latest-req-${index}`}>
                                                {line.name}{' '}
                                                <span className="text-[#141414]/55">
                                                    (×{line.quantity})
                                                </span>
                                            </li>
                                        )
                                    )}
                                </ul>
                            )}
                        </div>
                    )}
                    {latestDocument && parsedDataTab === 'free_issued' && (
                        <div>
                            {(latestDocument.extracted_data.free_issued_equipment ?? [])
                                .length === 0 ? (
                                <p className="ff-mono text-xs text-[#141414]/55">
                                    None listed.
                                </p>
                            ) : (
                                <ul className="list-disc list-inside space-y-1.5 ff-mono text-xs text-[#141414]">
                                    {latestDocument.extracted_data.free_issued_equipment.map(
                                        (line, index) => (
                                            <li key={`latest-free-${index}`}>
                                                {line.name}{' '}
                                                <span className="text-[#141414]/55">
                                                    (×{line.quantity})
                                                </span>
                                            </li>
                                        )
                                    )}
                                </ul>
                            )}
                        </div>
                    )}
                    {latestDocument && parsedDataTab === 'equipment_approved' && (
                        <div>
                            {(latestDocument.extracted_data.equipment_approved_for_use ?? [])
                                .length === 0 ? (
                                <p className="ff-mono text-xs text-[#141414]/55">
                                    None listed.
                                </p>
                            ) : (
                                <ul className="list-disc list-inside space-y-1.5 ff-mono text-xs text-[#141414]">
                                    {latestDocument.extracted_data.equipment_approved_for_use.map(
                                        (line, index) => (
                                            <li key={`latest-approved-${index}`}>
                                                {line.name}{' '}
                                                <span className="text-[#141414]/55">
                                                    (×{line.quantity})
                                                </span>
                                            </li>
                                        )
                                    )}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </SectionCard>

            <ConveyorStationsConfig
                key={stationsReloadKey}
                rfqId={rfqId}
                rfqTitle={rfq.title}
            />
            </>
            )}
        </div>
    );
};

const DetailsTab: React.FC<{
    active: boolean;
    onClick: () => void;
    label: string;
}> = ({ active, onClick, label }) => (
    <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={onClick}
        className={`ff-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border-l first:border-l-0 border-[#141414]/15 transition ${
            active
                ? 'bg-[#141414] text-[#f3efe6]'
                : 'text-[#141414]/70 hover:bg-[#141414]/5'
        }`}
    >
        {label}
    </button>
);

export default RFQDetails;
